import React, { useState, useEffect, useRef } from "react";
import "./TelemedicineConsentForm.css"; // We'll include CSS inline or separately

const TelemedicineConsentForm = () => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [lastPos, setLastPos] = useState({ x: 0, y: 0 });
  const [formData, setFormData] = useState({
    patientName: "",
    dob: "",
    email: "",
    phone: "",
    legalRep: "",
    relationship: "",
    facility: "",
    typedSig: "",
    signedByRole: "",
    consentDate: "",
    consentTime: "",
    sigImage: "",
    timezone: "",
    userAgent: "",
    timestampISO: "",
    ipAddress: "",
  });
  const [acknowledgements, setAcknowledgements] = useState({
    ack1: false,
    ack2: false,
    ack3: false,
    ack4: false,
    ack5: false,
    ack6: false,
    ack7: false,
  });
  const [showSuccess, setShowSuccess] = useState(false);

  // Auto-fill date, time, timezone, user agent
  useEffect(() => {
    const now = new Date();
    const pad = (n) => (n < 10 ? "0" + n : n);
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const offset = -now.getTimezoneOffset();
    const sign = offset >= 0 ? "+" : "-";
    const absOffset = Math.abs(offset);
    const hh = pad(Math.floor(absOffset / 60));
    const mm = pad(absOffset % 60);
    const iso = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
      now.getDate()
    )}T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(
      now.getSeconds()
    )}${sign}${hh}:${mm}`;
    const time = `${pad(now.getHours())}:${pad(now.getMinutes())}`;

    setFormData((prev) => ({
      ...prev,
      consentDate: prev.consentDate || now.toISOString().slice(0, 10),
      consentTime: time,
      timezone: tz,
      userAgent: navigator.userAgent,
      timestampISO: iso,
    }));
  }, []);

  // Check if submit should be enabled
  const isSubmitEnabled =
    Object.values(acknowledgements).every(Boolean) &&
    formData.typedSig.trim().length >= 2 &&
    formData.signedByRole !== "";

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle checkbox change
  const handleAckChange = (e) => {
    const { id, checked } = e.target;
    setAcknowledgements((prev) => ({ ...prev, [id]: checked }));
  };

  // Signature Pad Logic
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#000";

    const getPos = (e) => {
      const rect = canvas.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      return {
        x: clientX - rect.left,
        y: clientY - rect.top,
      };
    };

    const startDrawing = (e) => {
      setIsDrawing(true);
      const pos = getPos(e);
      setLastPos(pos);
      e.preventDefault();
    };

    const draw = (e) => {
      if (!isDrawing) return;
      const pos = getPos(e);
      ctx.beginPath();
      ctx.moveTo(lastPos.x, lastPos.y);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      setLastPos(pos);
      e.preventDefault();
    };

    const stopDrawing = () => {
      if (isDrawing) {
        setIsDrawing(false);
        setFormData((prev) => ({
          ...prev,
          sigImage: canvas.toDataURL("image/png"),
        }));
      }
    };

    // Mouse events
    canvas.addEventListener("mousedown", startDrawing);
    canvas.addEventListener("mousemove", draw);
    window.addEventListener("mouseup", stopDrawing);

    // Touch events
    canvas.addEventListener("touchstart", startDrawing, { passive: false });
    canvas.addEventListener("touchmove", draw, { passive: false });
    canvas.addEventListener("touchend", stopDrawing);

    return () => {
      canvas.removeEventListener("mousedown", startDrawing);
      canvas.removeEventListener("mousemove", draw);
      window.removeEventListener("mouseup", stopDrawing);
      canvas.removeEventListener("touchstart", startDrawing);
      canvas.removeEventListener("touchmove", draw);
      canvas.removeEventListener("touchend", stopDrawing);
    };
  }, [isDrawing, lastPos]);

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setFormData((prev) => ({ ...prev, sigImage: "" }));
  };

  const handlePrint = () => {
    // 1. Create a hidden iframe
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);

    // 2. Wait for iframe to load
    iframe.onload = () => {
      const doc = iframe.contentDocument || iframe.contentWindow.document;

      // 3. Copy ONLY the <main> content (your form)
      const mainContent = document.querySelector("main")?.cloneNode(true);
      if (!mainContent) return;

      // 4. Build minimal HTML with your original styles
      const styleSheets = Array.from(document.styleSheets)
        .map((sheet) => {
          try {
            if (sheet.href) {
              return `<link rel="stylesheet" href="${sheet.href}" />`;
            }
            const rules = Array.from(sheet.cssRules)
              .map((r) => r.cssText)
              .join("\n");
            return `<style>${rules}</style>`;
          } catch (e) {
            return "";
          }
        })
        .join("");

      const printHTML = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <title>Telemedicine Consent</title>
        ${styleSheets}
        <style>
          @media print {
            body { margin: 0; padding: 1rem; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; }
            .actions, .footer, button, .btn { display: none !important; }
          }
          body { background: #fff; }
        </style>
      </head>
      <body>${mainContent.outerHTML}</body>
      </html>
    `;

      doc.open();
      doc.write(printHTML);
      doc.close();

      // 5. Trigger print
      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();

        // 6. Clean up after print dialog closes
        const removeIframe = () => {
          document.body.removeChild(iframe);
          iframe.contentWindow.removeEventListener("afterprint", removeIframe);
        };
        iframe.contentWindow.addEventListener("afterprint", removeIframe);
      }, 250);
    };

    // Trigger iframe load
    iframe.src = "about:blank";
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Capture signature if not already saved
    if (!formData.sigImage && canvasRef.current) {
      setFormData((prev) => ({
        ...prev,
        sigImage: canvasRef.current.toDataURL("image/png"),
      }));
    }

    // Simulate submission
    console.log("Form submitted:", { ...formData, acknowledgements });
    setShowSuccess(true);

    // In real app: POST to backend
    // fetch('/your-secure-endpoint', { method: 'POST', body: new FormData(...) })
  };

  const handleReset = () => {
    setFormData({
      patientName: "",
      dob: "",
      email: "",
      phone: "",
      legalRep: "",
      relationship: "",
      facility: "",
      typedSig: "",
      signedByRole: "",
      consentDate: "",
      consentTime: "",
      sigImage: "",
      timezone: "",
      userAgent: "",
      timestampISO: "",
      ipAddress: "",
    });
    setAcknowledgements({
      ack1: false,
      ack2: false,
      ack3: false,
      ack4: false,
      ack5: false,
      ack6: false,
      ack7: false,
    });
    setShowSuccess(false);
    clearSignature();
  };

  return (
    <>
      <header>
        <div className="wrap">
          <img
            alt="Logo"
            src="data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='56' height='56'><rect width='56' height='56' rx='8' fill='%23ffffff'/><text x='50%' y='54%' text-anchor='middle' font-family='Arial' font-size='20' fill='%230f62fe'>HL</text></svg>"
          />
          <div>
            <h1>
              Telemedicine Consent for Pre‑Move Evaluation (PME){" "}
              <span className="pill">Click‑to‑Consent</span>
            </h1>
            <div className="small">
              For assisted living placement evaluations conducted online via
              secure telehealth.
            </div>
          </div>
        </div>
      </header>

      <main>
        <form id="consentForm" onSubmit={handleSubmit} noValidate>
          <section>
            <div className="disclaimer">
              <strong>Important:</strong> This telemedicine consent confirms you
              understand the nature, risks, benefits, and limits of an online
              PME. This evaluation supports assisted living placement decisions
              but <em>does not</em> replace emergency care or ongoing treatment
              from your physicians. If you are experiencing an emergency, call
              911 immediately.
            </div>
          </section>

          <section>
            <h2>Patient & Contact Information</h2>
            <div className="grid">
              <div>
                <label htmlFor="patientName">
                  Patient Full Name<span className="req">*</span>
                </label>
                <input
                  id="patientName"
                  name="patientName"
                  type="text"
                  value={formData.patientName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div>
                <label htmlFor="dob">
                  Date of Birth<span className="req">*</span>
                </label>
                <input
                  id="dob"
                  name="dob"
                  type="date"
                  value={formData.dob}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div>
                <label htmlFor="email">
                  Email<span className="req">*</span>
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div>
                <label htmlFor="phone">
                  Phone<span className="req">*</span>
                </label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div>
                <label htmlFor="legalRep">
                  Legal Representative (if applicable)
                </label>
                <input
                  id="legalRep"
                  name="legalRep"
                  type="text"
                  placeholder="Name of guardian/POA/health proxy"
                  value={formData.legalRep}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <label htmlFor="relationship">Relationship</label>
                <input
                  id="relationship"
                  name="relationship"
                  type="text"
                  placeholder="e.g., Daughter, POA, Guardian"
                  value={formData.relationship}
                  onChange={handleInputChange}
                />
              </div>
              <div className="full">
                <label htmlFor="facility">
                  Intended Assisted Living Facility
                </label>
                <input
                  id="facility"
                  name="facility"
                  type="text"
                  placeholder="Facility name (optional)"
                  value={formData.facility}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </section>

          <section>
            <h2>Consent Disclosures</h2>
            {[
              {
                title: "Nature & Purpose of Telemedicine",
                content: "The PME is performed via secure audio/video...",
              },
              {
                title: "Risks & Limitations",
                content: "Telemedicine may be limited by technology issues...",
              },
              {
                title: "Privacy & Security (HIPAA)",
                content:
                  "Your information will be safeguarded using HIPAA‑compliant platforms...",
              },
              {
                title: "Data Sharing Authorization",
                content: "You authorize us to share the PME summary...",
              },
              {
                title: "Patient Rights",
                content:
                  "You may ask questions; decline or withdraw consent...",
              },
              {
                title: "Emergency & Ongoing Care",
                content: "Telemedicine PME is not emergency care...",
              },
              {
                title: "Financial Responsibility",
                content: "You are responsible for PME fees as posted...",
              },
              {
                title: "Limitation of Liability",
                content: "To the fullest extent permitted by law...",
              },
              {
                title: "E‑Signature Consent (ESIGN)",
                content: "By typing your name and/or drawing your signature...",
              },
            ].map((item, i) => (
              <details key={i} open={i === 0}>
                <summary>{item.title}</summary>
                <div className="muted">{item.content}</div>
              </details>
            ))}
          </section>

          <section>
            <h2>Required Acknowledgements</h2>
            <div className="ack-list">
              {[
                "I have read and understand the Telemedicine Nature & Purpose disclosure.",
                "I understand the Risks & Limitations and that an in‑person evaluation may be required.",
                "I acknowledge the HIPAA Privacy & Security statement and authorize necessary data sharing...",
                "I understand the Emergency & Ongoing Care statement and that this PME is not emergency care.",
                "I accept the Financial Responsibility terms.",
                "I accept the Limitation of Liability.",
                "I consent to Electronic Signatures (ESIGN) for this PME.",
              ].map((text, i) => (
                <label key={i} className="checkbox">
                  <input
                    type="checkbox"
                    id={`ack${i + 1}`}
                    checked={acknowledgements[`ack${i + 1}`]}
                    onChange={handleAckChange}
                    required
                  />
                  <span>{text}</span>
                </label>
              ))}
            </div>
          </section>

          <section>
            <h2>Consent & Signature</h2>
            <div className="grid">
              <div>
                <label htmlFor="typedSig">
                  Type Full Name (e‑signature)<span className="req">*</span>
                </label>
                <input
                  id="typedSig"
                  name="typedSig"
                  type="text"
                  value={formData.typedSig}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div>
                <label htmlFor="signedByRole">
                  Signer Role<span className="req">*</span>
                </label>
                <select
                  id="signedByRole"
                  name="signedByRole"
                  value={formData.signedByRole}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select…</option>
                  <option>Patient</option>
                  <option>Parent/Guardian</option>
                  <option>POA/Health Proxy</option>
                  <option>Other Representative</option>
                </select>
              </div>
              <div>
                <label htmlFor="consentDate">Date</label>
                <input
                  id="consentDate"
                  name="consentDate"
                  type="date"
                  value={formData.consentDate}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <label htmlFor="consentTime">Time</label>
                <input
                  id="consentTime"
                  name="consentTime"
                  type="text"
                  placeholder="Auto‑filled"
                  value={formData.consentTime}
                  readOnly
                />
              </div>
              <div className="full">
                <label>Draw Signature (optional)</label>
                <canvas
                  ref={canvasRef}
                  className="sig-pad"
                  width={700}
                  height={160}
                ></canvas>
                <div className="actions">
                  <button
                    type="button"
                    className="secondary"
                    onClick={clearSignature}
                  >
                    Clear
                  </button>
                </div>
                <input
                  type="hidden"
                  name="sigImage"
                  value={formData.sigImage}
                />
              </div>
            </div>
            <div className="muted small">
              By signing, you affirm you are authorized to provide consent for
              this patient.
            </div>
          </section>

          <section>
            <div className="actions">
              <button
                type="submit"
                className="primary"
                disabled={!isSubmitEnabled}
              >
                I Agree &amp; Sign
              </button>
              <button type="button" className="secondary" onClick={handlePrint}>
                Print / Save as PDF
              </button>
              <button type="button" className="danger" onClick={handleReset}>
                Reset
              </button>
            </div>
            {showSuccess && (
              <div className="footer">
                <div
                  className="success"
                  style={{
                    padding: "10px 12px",
                    borderRadius: "8px",
                    marginTop: "12px",
                  }}
                >
                  Consent recorded locally. Be sure to configure your form
                  endpoint to store records securely.
                </div>
              </div>
            )}
          </section>

          {/* Hidden Fields */}
          <input type="hidden" name="timezone" value={formData.timezone} />
          <input type="hidden" name="userAgent" value={formData.userAgent} />
          <input
            type="hidden"
            name="timestampISO"
            value={formData.timestampISO}
          />
          <input type="hidden" name="ipAddress" value={formData.ipAddress} />
        </form>

        <div className="footer">
          <strong>Implementation Notes (remove before publishing):</strong>
          <ul>
            <li>
              Set the form action to your secure endpoint (e.g., EMR intake,
              HIPAA‑compliant form handler) and use HTTPS.
            </li>
            <li>
              Store the signature image (base64) and typed signature with
              timestamp, TZ, and user agent; capture IP on the server.
            </li>
            <li>
              Link your Notice of Privacy Practices (NPP) above and add your
              logo in the header.
            </li>
          </ul>
        </div>
      </main>
    </>
  );
};

export default TelemedicineConsentForm;
